import { type Components, type Theme } from '@mui/material';
import { CheckFilledIcon, ErrorFilledIcon, InfoFilledIcon, InfoOutlinedIcon, WarningFilledIcon } from 'icons';

import { blue, common, dark, green, grey, orange, red } from '../palette';

export const MuiAlert: Components<Theme>['MuiAlert'] = {
  defaultProps: {
    variant: 'standard',
    iconMapping: {
      success: <CheckFilledIcon fontSize="inherit" />,
      error: <ErrorFilledIcon fontSize="inherit" />,
      warning: <WarningFilledIcon fontSize="inherit" />,
      info: <InfoFilledIcon fontSize="inherit" />,
      infoGray: <InfoFilledIcon fontSize="inherit" />,
      infoDark: <InfoFilledIcon fontSize="inherit" />,
      white: <InfoOutlinedIcon fontSize="inherit" />,
    },
  },
  styleOverrides: {
    root: ({ theme: { spacing, typography, palette, shape } }) => ({
      ...typography['natter-text-sm'],
      border: 'none',
      borderRadius: shape.borderRadius * 2,
      padding: spacing(2),

      '&:not(.MuiAlert-filled)': {
        color: palette.grey[900],
      },

      '&:has(.MuiAlertTitle-root) .MuiAlert-icon': {
        fontSize: 24,
        marginRight: spacing(2),
      },

      '& .MuiAlertTitle-root': {
        fontWeight: 600,
      },

      '& .MuiAlert-action .MuiSvgIcon-root': {
        fontSize: 24,
        color: palette.primary.main,
      },
    }),
    filled: ({ theme: { palette } }) => ({
      color: palette.common.white,

      '&:not(.MuiAlert-filled)': {
        color: palette.common.white,
      },

      '& .MuiAlert-action .MuiSvgIcon-root': {
        fontSize: 24,
        color: palette.common.white,
      },
    }),
    icon: ({ theme: { spacing } }) => ({
      fontSize: 20,
      marginRight: spacing(1.5),
      padding: 0,
      justifyContent: 'center',
    }),
    action: {
      paddingTop: 0,
      marginRight: 0,
    },
    message: {
      color: grey[800],
      padding: 0,
      whiteSpace: 'pre-wrap',
    },
    standardSuccess: {
      backgroundColor: green[50],
      color: 'inherit',

      '& .MuiAlert-icon': {
        color: green[600],
      },
    },
    standardError: {
      backgroundColor: red[50],
      color: 'inherit',

      '& .MuiAlert-icon': {
        color: red[500],
      },
    },
    standardInfo: {
      backgroundColor: blue[50],

      '& .MuiAlert-icon': {
        color: blue[600],
      },
    },
    standardWarning: {
      backgroundColor: orange[50],
      color: grey[800],

      '& .MuiAlert-icon': {
        color: orange[600],
      },
    },
    filledError: {
      backgroundColor: red[700],
      color: common.white,

      '& .MuiAlert-message': {
        color: common.white,
      },

      '& .MuiAlert-icon': {
        color: common.white,
      },
    },
  },
  variants: [
    {
      props: { severity: 'white' },
      style: {
        color: grey[800],
        backgroundColor: 'white',
        border: `1px solid ${grey[300]}`,
      },
    },
    {
      props: { severity: 'infoGray' },
      style: {
        backgroundColor: grey[50],
        color: grey[800],
      },
    },
    {
      props: { severity: 'infoDark' },
      style: {
        backgroundColor: dark[50],
        '&.MuiAlert-root:not(.MuiAlert-filled)': {
          color: grey[200],
        },
        '& .MuiAlert-message': {
          color: 'inherit',
        },
      },
    },
  ],
};

export const MuiAlertTitle: Components<Theme>['MuiAlertTitle'] = {
  defaultProps: {
    variant: 'natter-text-md',
    color: 'grey.900',
  },
  styleOverrides: {
    root: ({ theme: { spacing } }) => ({
      marginBottom: spacing(1),
    }),
  },
};

declare module '@mui/material/Alert' {
  interface AlertPropsColorOverrides {
    infoGray: true;
    infoDark: true;
    white: true;
  }
}
